import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Link, useParams } from "wouter";
import { Helm } from "./helm.js";
import { OrgSync, AccountNav } from "./components.js";
import { CommandPalette } from "./command_palette.js";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Book,
  Command,
  Download,
  House,
  ListBullets,
  Upload,
} from "@phosphor-icons/react";

interface HelmLayoutProps {
  children: ReactNode;
}

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

const openCommandPalette = () => {
  const event = new KeyboardEvent("keydown", {
    key: "k",
    metaKey: isMac,
    ctrlKey: !isMac,
    bubbles: true,
  });
  window.dispatchEvent(event);
};

const MenuNav = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();

  return (
    <div className="flex items-center justify-end mr-4">
      <NavigationMenu viewport={false}>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger>Menu</NavigationMenuTrigger>
            <NavigationMenuContent className="left-auto right-0">
              <ul className="w-48">
                <li>
                  <NavigationMenuLink asChild>
                    <button
                      onClick={openCommandPalette}
                      className="flex w-full items-center justify-between gap-2"
                    >
                      <span className="flex items-center gap-2">
                        <Command className="size-4" />
                        Command Menu
                      </span>
                      <kbd className="inline-flex items-center gap-0.5 rounded-none border border-border bg-muted px-1 py-0.5 font-mono text-[10px]">
                        {isMac ? "⌘" : "Ctrl"}K
                      </kbd>
                    </button>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <Link
                      href={`/o/${orgSlug}/actions`}
                      className="flex items-center gap-2"
                    >
                      <ListBullets className="size-4" />
                      Action Log
                    </Link>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <Link
                      href={`/o/${orgSlug}/sources`}
                      className="flex items-center gap-2"
                    >
                      <Download className="size-4" />
                      Sources
                    </Link>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <Link
                      href={`/o/${orgSlug}/channels`}
                      className="flex items-center gap-2"
                    >
                      <Upload className="size-4" />
                      Channels
                    </Link>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <Link href="/docs" className="flex items-center gap-2">
                      <Book className="size-4" />
                      Docs
                    </Link>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <Link href="/" className="flex items-center gap-2">
                      <House className="size-4" />
                      Landing Page
                    </Link>
                  </NavigationMenuLink>
                </li>
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    </div>
  );
};

export const HelmLayout = ({ children }: HelmLayoutProps) => {
  return (
    <OrgSync>
      <CommandPalette />
      <div className="sticky top-0 z-10 grid grid-cols-[1fr_auto_1fr] bg-white shadow">
        <AccountNav />
        <Helm />
        <MenuNav />
      </div>
      <div className="mx-auto max-w-4xl overflow-y-auto p-6">{children}</div>
    </OrgSync>
  );
};

interface MarketingLayoutProps {
  children: ReactNode;
}

const MarketingHeader = () => {
  return (
    <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link href="/" className="text-xl font-semibold tracking-tight">
          Scantling
        </Link>
        <nav className="flex items-center gap-6">
          <Link href="/docs" className="text-sm text-gray-600 hover:text-black">
            Docs
          </Link>
          <Button asChild>
            <Link href="/login">Sign In</Link>
          </Button>
        </nav>
      </div>
    </header>
  );
};

const MarketingFooter = () => {
  return (
    <footer className="border-t border-gray-200 bg-gray-50">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-sm text-gray-600">Scantling</p>
          <nav className="flex gap-6">
            <Link
              href="/docs"
              className="text-sm text-gray-600 hover:text-black"
            >
              Docs
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export const MarketingLayout = ({ children }: MarketingLayoutProps) => {
  return (
    <div className="flex min-h-screen flex-col">
      <MarketingHeader />
      <main className="flex-1">{children}</main>
      <MarketingFooter />
    </div>
  );
};

interface DocLayoutProps {
  children: ReactNode;
}

const DocHeader = () => {
  return (
    <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-6">
          <Link href="/" className="text-xl font-semibold tracking-tight">
            Scantling
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            href="/docs"
            className="text-sm font-medium text-gray-600 hover:text-black"
          >
            Docs
          </Link>
        </div>
        <nav className="flex items-center gap-6">
          <Button asChild>
            <Link href="/login">Sign In</Link>
          </Button>
        </nav>
      </div>
    </header>
  );
};

const DocFooter = () => {
  return (
    <footer className="border-t border-gray-200 bg-gray-50">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-sm text-gray-600">Scantling Docs</p>
          <nav className="flex gap-6">
            <Link href="/" className="text-sm text-gray-600 hover:text-black">
              Home
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export const DocLayout = ({ children }: DocLayoutProps) => {
  return (
    <div className="flex min-h-screen flex-col">
      <DocHeader />
      <main className="flex-1">{children}</main>
      <DocFooter />
    </div>
  );
};
